/**
 * OfflineIndicator — a calm, always-honest banner showing network + sync state.
 *
 * Methodology hold: never silent fails. When the user loses connection, we
 * tell them — and we tell them their work is safe.
 *
 * Apr 29 2026 follow-up #4 — Lead's call: "Install button is always in the
 * way like why?" The PWA install pill now has an inline ✕ dismiss button;
 * dismissing stores `tsogn_install_dismissed` in localStorage so the pill
 * stays hidden for 14 days. The pill is also moved to the bottom-RIGHT
 * (out of the way of left-side content like the demo's BACK TO TIME
 * MACHINE) and shrunk to a compact icon-only visual on mobile.
 */
import { useState, useEffect } from 'react';
import { WifiOff, Loader2, CheckCircle2, Download, RefreshCw, AlertTriangle, X } from 'lucide-react';
import { useOnline } from '../context/OnlineContext';

const INSTALL_DISMISS_KEY = 'tsogn_install_dismissed_until';
const INSTALL_DISMISS_DAYS = 14;

export default function OfflineIndicator() {
  const { online, pendingCount, failedSyncCount, syncing, installable, triggerInstall, updateReady, applyUpdate } = useOnline();
  const [installDismissed, setInstallDismissed] = useState(false);

  useEffect(() => {
    try {
      const until = parseInt(localStorage.getItem(INSTALL_DISMISS_KEY) || '0', 10);
      if (until > Date.now()) setInstallDismissed(true);
    } catch { /* noop */ }
  }, []);

  const dismissInstall = (e) => {
    e?.stopPropagation?.();
    try {
      localStorage.setItem(INSTALL_DISMISS_KEY, String(Date.now() + INSTALL_DISMISS_DAYS * 24 * 3600 * 1000));
    } catch { /* noop */ }
    setInstallDismissed(true);
  };

  if (online && pendingCount === 0 && failedSyncCount === 0 && !syncing && !installable && !updateReady) return null;
  // If only the install pill would show but it's been dismissed, hide the
  // whole banner cluster so nothing covers the corner.
  const onlyInstall = !updateReady && online && !syncing && pendingCount === 0 && failedSyncCount === 0 && installable;
  if (onlyInstall && installDismissed) return null;

  return (
    <div className="fixed bottom-3 right-3 z-[9998] flex flex-col gap-2 items-end" data-testid="offline-indicator">
      {updateReady && (
        <div
          className="border border-[#2F8A92]/40 bg-[#0A0A0A]/95 rounded px-3 py-2 flex items-center gap-2 shadow-xl"
          data-testid="sw-update-banner"
        >
          <RefreshCw size={12} className="text-[#2F8A92]" />
          <span className="font-hud text-[10px] tracking-wider text-white/80">UPDATE READY</span>
          <button
            onClick={applyUpdate}
            className="font-hud text-[10px] tracking-wider text-[#2F8A92] hover:text-[#66FAFF] underline"
            data-testid="sw-update-apply"
          >
            RELOAD
          </button>
        </div>
      )}

      {!online && (
        <div
          className="border border-[#D4A84A]/40 bg-[#0A0A0A]/95 rounded px-3 py-2 flex items-center gap-2 shadow-xl max-w-xs"
          data-testid="offline-banner"
        >
          <WifiOff size={12} className="text-[#D4A84A] flex-shrink-0" />
          <div className="min-w-0">
            <div className="font-hud text-[10px] tracking-widest text-[#D4A84A] uppercase">OFFLINE — WORK SAFE LOCALLY</div>
            <div className="font-mono text-[9px] text-white/80 mt-0.5">
              Changes save to your device. They'll sync when the connection returns.
            </div>
          </div>
        </div>
      )}

      {online && (syncing || pendingCount > 0) && (
        <div
          className="border border-[#2F8A92]/30 bg-[#0A0A0A]/95 rounded px-3 py-2 flex items-center gap-2 shadow-xl"
          data-testid="sync-banner"
        >
          {syncing ? (
            <Loader2 size={12} className="text-[#2F8A92] animate-spin flex-shrink-0" />
          ) : (
            <CheckCircle2 size={12} className="text-[#2F8A92] flex-shrink-0" />
          )}
          <span className="font-hud text-[10px] tracking-wider text-white/80">
            {syncing ? 'SYNCING…' : `${pendingCount} CHANGE${pendingCount === 1 ? '' : 'S'} QUEUED`}
          </span>
        </div>
      )}

      {online && failedSyncCount > 0 && (
        <button
          onClick={() => {
            // Broadcast intent to open MyProfile → MainApp listens and switches view.
            // Falls back to hash navigation so it works even if listener is missing.
            try { window.dispatchEvent(new CustomEvent('tsogn:open-profile')); } catch {}
            window.location.hash = 'my-data-panel';
          }}
          className="border border-red-400/40 bg-[#0A0A0A]/95 rounded px-3 py-2 flex items-start gap-2 shadow-xl max-w-xs hover:bg-red-400/5 transition-all text-left"
          data-testid="sync-failed-banner"
        >
          <AlertTriangle size={12} className="text-red-400 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="font-hud text-[10px] tracking-widest text-red-400 uppercase">
              {failedSyncCount} CHANGE{failedSyncCount === 1 ? '' : 'S'} NEED ATTENTION
            </div>
            <div className="font-mono text-[9px] text-white/80 mt-0.5 flex items-center gap-1">
              Not dropped — preserved locally. <span className="text-[#D4A84A] underline">Download from My Profile</span>
            </div>
          </div>
        </button>
      )}

      {online && installable && !installDismissed && pendingCount === 0 && failedSyncCount === 0 && !syncing && (
        <div
          className="border border-[#D4A84A]/30 bg-[#0A0A0A]/95 rounded px-2.5 py-1.5 flex items-center gap-2 shadow-xl"
          data-testid="install-pwa-pill"
        >
          <button
            onClick={triggerInstall}
            className="inline-flex items-center gap-1.5 hover:opacity-80 transition-opacity"
            data-testid="install-pwa-btn"
          >
            <Download size={12} className="text-[#D4A84A]" />
            <span className="font-hud text-[10px] tracking-widest text-[#D4A84A] uppercase hidden md:inline">INSTALL TSOGN</span>
            <span className="font-hud text-[10px] tracking-widest text-[#D4A84A] uppercase md:hidden">INSTALL</span>
          </button>
          <button
            onClick={dismissInstall}
            className="text-white/40 hover:text-white/80 transition-colors -mr-0.5"
            data-testid="install-pwa-dismiss"
            aria-label="Dismiss install prompt"
            title="Hide for 14 days"
          >
            <X size={12} />
          </button>
        </div>
      )}
    </div>
  );
}
